import {
  Alert,
  Button,
  Card,
  Dropdown,
  Spin,
  Tooltip,
  Typography,
} from 'antd';
import {
  CheckCircleOutlined,
  ClockCircleOutlined,
  CloseCircleOutlined,
  LoadingOutlined,
  MoreOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import React, { useEffect, useRef, useState } from 'react';
import isEqual from 'lodash/isEqual';
import { FormValues } from '@ynomia/dynamic-form';
import { models } from '@ynomia/core';
import { ModelViewer } from '../../atoms';
import {
  getAssetTypes,
  getColorSchemeForStatus,
  getFilteredTwinData,
  getScratchProjectCode,
  getSelectedAssetType,
  getTenant,
} from '../../../selectors';
import {
  setIsLoadingPercentage,
  setIsTwinLoading,
  setIsTwinReady,
  setSelectedAssetTypeId,
} from '../../../actions';
import AssetTypeDropdown from '../AssetTypeDropdown';
import ModalForm from '../../atoms/ModalForm';
import {
  affectsMapping,
  mappingConfigFromForm,
  mappingFiltrexFunctions,
  mappingFormFields,
  mappingFormValues,
} from './mappingConfig';
import {
  StatusTone,
  canRemap,
  describeStatus,
  remapUnavailableReason,
} from './modelStatus';
import { PRESET_TWIN_ID_NAME } from '../../../config/constants';
import config from '../../../config';
import { getContextStores } from '../../../context';
import { modal, notification } from '../../../antdProvider';
import { formatDate } from '../../../utils';
import styles from './styles.module.less';

export type { ApsModel } from './types';

export interface Props {
  modelId: string,
  modelsKeyedById: Record<string, models.ModelResponse>,
  disableUpdate: boolean,
  updateModel: (id: string, model: Partial<models.Model>) => Promise<void>,
  remapModel: (id: string) => Promise<void>,
}

const STATUS_ICONS: Record<StatusTone, React.ReactNode> = {
  progress: <LoadingOutlined />,
  queued: <ClockCircleOutlined style={{ color: 'grey' }} />,
  success: <CheckCircleOutlined style={{ color: 'green' }} />,
  warning: <WarningOutlined style={{ color: 'orange' }} />,
  error: <CloseCircleOutlined style={{ color: 'red' }} />,
};

const STATUS_COLORS: Record<StatusTone, string> = {
  progress: 'grey',
  queued: 'grey',
  success: 'green',
  warning: 'orange',
  error: 'red',
};

const ModelEditor = ({
  modelId,
  modelsKeyedById,
  disableUpdate,
  updateModel,
  remapModel,
}: Props) => {
  /* Context */
  const contextStores = getContextStores();

  /* Selectors */
  const twinData = getFilteredTwinData(contextStores);
  const colorScheme = getColorSchemeForStatus(contextStores);
  const project = getScratchProjectCode(contextStores);
  const tenant = getTenant(contextStores);

  const assetTypes = getAssetTypes(contextStores);
  const { digitalTwinDispatch, assetManagerDispatch, assetState } = contextStores;
  const { assetManagerId } = contextStores.featureState;
  const currentType = getSelectedAssetType(contextStores);
  const { twin } = currentType;
  const forgeConfig = twin?.forge;
  const [saving, setSaving] = useState<boolean>(false);
  const [remapping, setRemapping] = useState<boolean>(false);
  const [reloadToken, setReloadToken] = useState<number>(0);

  const { host } = config;
  const model = modelsKeyedById[modelId];
  const status = describeStatus(model.status);

  /* Default the "Preview Asset Type" to the model's first enabled asset type. */
  const enabledAssetTypesKey = (model.assetTypes || []).join(',');
  useEffect(() => {
    const [firstEnabledType] = model.assetTypes || [];
    if (firstEnabledType && !model.assetTypes.includes(currentType.id)) {
      setSelectedAssetTypeId(firstEnabledType, assetManagerId, assetManagerDispatch);
    }
  }, [modelId, enabledAssetTypesKey]);

  /**
   * Reload the preview when a new mapping lands, so it shows what was just built. Only for the
   * model already open: on mount, or on switching models, the viewer is loading it anyway, and
   * a reload would restart that load part way through.
   */
  const lastMapping = useRef<{ modelId: string, mappedAt?: models.Model['mappedAt'] }>();
  useEffect(() => {
    const previous = lastMapping.current;
    lastMapping.current = { modelId, mappedAt: model.mappedAt };
    if (previous?.modelId === modelId && previous.mappedAt !== model.mappedAt) {
      setReloadToken(t => t + 1);
    }
  }, [modelId, model.mappedAt]);

  /**
   * The assets set each element's colour and visibility, so a twin painted before they arrive
   * shows up uncoloured or hidden. Wait for the first fetch only: later refreshes repaint a
   * mounted viewer, and unmounting it for them would reload the model.
   */
  const assetsReady = assetState.initialAssetFetchComplete;

  const onTwinLoading = (percentage) => {
    /**
     * Percentage goes backwards sometimes (ie. from 100% -> 95%)
     * This check prevents the loading bar from showing again.
     * */
    if (percentage === 0) {
      setIsTwinLoading(true, digitalTwinDispatch);
    }

    setIsLoadingPercentage(percentage, digitalTwinDispatch);

    if (percentage === 100) {
      setIsTwinLoading(false, digitalTwinDispatch);
    }
  };

  const onRemap = async () => {
    setRemapping(true);
    try {
      await remapModel(modelId);
    } finally {
      setRemapping(false);
    }
  };

  /**
   * Sends only what changed, so a save can't overwrite fields the server owns (`status`,
   * `mapping`, `mappedAt`) with the values this page loaded.
   */
  const buildUpdate = (submission: FormValues): Partial<models.Model> => {
    const update: Partial<models.Model> = {};
    const assetTypesSubmitted = submission.assetTypes as Array<string>;
    if (!isEqual([...assetTypesSubmitted].sort(), [...(model.assetTypes || [])].sort())) {
      update.assetTypes = assetTypesSubmitted;
    }
    if (!!submission.loadMappedIdsOnly !== !!model.loadMappedIdsOnly) {
      update.loadMappedIdsOnly = !!submission.loadMappedIdsOnly;
    }
    if (submission.twinIdField !== model.twinIdField) {
      update.twinIdField = submission.twinIdField as string;
    }
    const { mappingConfig, isolationMappingConfig } = mappingConfigFromForm(
      submission,
      assetTypes,
      assetTypesSubmitted,
    );
    if (!isEqual(mappingConfig, model.mappingConfig)) update.mappingConfig = mappingConfig;
    if (isolationMappingConfig && !isEqual(isolationMappingConfig, model.isolationMappingConfig)) {
      update.isolationMappingConfig = isolationMappingConfig;
    }
    return update;
  };

  const save = async (update: Partial<models.Model>, remap: boolean) => {
    setSaving(true);
    try {
      await updateModel(modelId, update);
      if (!remap) return;
      if (canRemap(model.status)) {
        await remapModel(modelId);
      } else if (model.status === 'mapping') {
        notification.info({
          message: 'Saved',
          description: 'A mapping run is already in progress with the previous settings. '
            + 'Re-map once it finishes to apply these.',
        });
      }
      // Otherwise the model hasn't been mapped yet, and its first run reads the saved settings.
    } catch (e: any) {
      notification.error({
        message: 'Could not save the model',
        description: e?.response?.data?.err?.msg || e?.message,
      });
    } finally {
      setSaving(false);
    }
  };

  const onSave = async (submission: FormValues) => {
    const update = buildUpdate(submission);
    if (!Object.keys(update).length) {
      notification.info({ message: 'No changes to save' });
      return;
    }
    const remap = affectsMapping(model, update);
    if (!remap) {
      await save(update, false);
      return;
    }
    modal.confirm({
      centered: true,
      title: 'Re-map this model?',
      content: 'These changes affect how the model is mapped. Saving re-maps it on the server, '
        + 'which can take a few minutes.',
      okText: 'Save and re-map',
      // Not returned, so the dialog closes straight away: the Save button's spinner and the
      // status line already show progress.
      onOk: () => { save(update, true); },
    });
  };

  return (
    <div className={styles.container}>
      <div className={styles.modelContainer}>
        {!assetsReady && (
          <div className={styles.previewPlaceholder}>
            <Spin size="large" />
            <Typography.Text type="secondary">Loading assets...</Typography.Text>
          </div>
        )}
        {assetsReady && (
          <ModelViewer
            ref={React.createRef()}
            source={host.twin}
            modelCode="settings"
            twinData={twinData}
            config={JSON.stringify(colorScheme)}
            loadModels={[modelId]}
            project={project}
            tenant={tenant}
            isTwinLoading={false}
            onTwinLoading={onTwinLoading}
            onTwinReady={(onTwinReady) => {
              setIsTwinReady(onTwinReady, digitalTwinDispatch);
            }}
            requestModelReload={reloadToken}
            forgeConfig={forgeConfig!}
          />
        )}

        <div className={styles.assetTypeDropdownContainer}>
          <Card title="Preview Asset Type" size="small">
            <AssetTypeDropdown size="small" />
          </Card>
        </div>
      </div>
      <div className={styles.settingsContainer}>
        <div className={styles.statusContainer}>
          <Typography.Title level={5} style={{ margin: 0 }}>
            Mapping Status
          </Typography.Title>
          <div className={styles.mappingStatusRow}>
            <Tooltip title={model.mappedAt && `Last mapped ${formatDate(model.mappedAt)}`}>
              <span style={{ color: status ? STATUS_COLORS[status.tone] : undefined }}>
                {status && STATUS_ICONS[status.tone]}
                {' '}
                {status?.label || 'Unknown'}
              </span>
            </Tooltip>
            {/* Tucked away: saving a mapping change already re-maps, so this is a fallback. */}
            <Dropdown
              trigger={['click']}
              disabled={disableUpdate || saving || remapping}
              menu={{
                items: [{
                  key: 'remap',
                  label: (
                    <Tooltip title={remapUnavailableReason(model.status)} placement="left">
                      Re-map
                    </Tooltip>
                  ),
                  disabled: !canRemap(model.status),
                  onClick: onRemap,
                }],
              }}
            >
              <Button
                type="text"
                size="small"
                icon={remapping ? <LoadingOutlined /> : <MoreOutlined />}
                aria-label="More actions"
              />
            </Dropdown>
          </div>
          {status?.failed && model.statusError && (
            <Alert
              type={status.tone === 'error' ? 'error' : 'warning'}
              message={model.statusError}
            />
          )}
        </div>
        <Typography.Title level={5} style={{ marginTop: 16 }}>
          Properties
        </Typography.Title>
        {/* The only part that scrolls: it fills whatever height the status above leaves. */}
        <div className={styles.propertiesContainer}>
          <ModalForm
            fields={[
              {
                entryComponent: 'picklist',
                id: 'assetTypes',
                label: 'Enabled Asset Type(s)',
                properties: {
                  options: assetTypes.map(assetType => ({
                    label: assetType.label_pluralized_long,
                    value: assetType.id,
                  })),
                  multi: true,
                },
                isRequired: true,
              },
              {
                entryComponent: 'checkbox',
                id: 'loadMappedIdsOnly',
                label: 'Hide non-mapped elements for users to better performance'
                  + ' (Must be enabled for advanced filters to work)',
              },
              {
                label: 'divider',
                id: 'mappingDivider',
                entryComponent: 'divider',
              },
              {
                entryComponent: 'picklist',
                id: 'twinIdField',
                label: 'Twin ID Property Key',
                properties: {
                  options: PRESET_TWIN_ID_NAME.map(label => ({
                    label,
                    value: label,
                  })),
                  tags: true,
                  maxCount: 1,
                },
                isRequired: true,
              },
              ...mappingFormFields(assetTypes),
            ]}
            submitButtonText="Save"
            onSubmit={onSave}
            hideCancelButton
            defaultValues={{
              assetTypes: model.assetTypes,
              loadMappedIdsOnly: model.loadMappedIdsOnly,
              ...mappingFormValues(model),
            }}
            submitButtonLoading={saving}
            isDisabled={disableUpdate}
            extraFiltrexFunctions={mappingFiltrexFunctions}
            scrollable={false}
          />
        </div>
      </div>
    </div>
  );
};
export default ModelEditor;
